import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEventHandler,
  type KeyboardEvent,
  type PropsWithChildren,
} from 'react';
import { useDebounce } from 'react-use';
import type { CSSProperties } from 'styled-components';
import { useMixpanel } from '../../contexts';
import { useDynamicCallback, useOnKeyDown } from '../../hooks';
import { MixpanelEvent, MixpanelEventProperty, MixpanelEventSource } from '../../tokens';
import { HStack, Stack } from '../Core';
import { Divider } from '../Divider';
import { FormControlSizes, Input } from '../Form';
import { Kbd } from '../Kbd';
import { Modal, type ModalProps } from '../Modal/Modal';
import { Text } from '../Text';
import { PowerSearchActiveGroupContext } from './PowerSearchActiveGroupContext';
import { usePowerSearchContext } from './PowerSearchContext';
import { PowerSearchHighlightContext } from './PowerSearchHighlightContext';
import { PowerSearchShortcut } from './PowerSearchShortcut';

const INPUT_STYLES: CSSProperties = { border: '0', paddingLeft: 16 };
export const POWER_SEARCH_PLACEHOLDER = 'Search for orders, symbols, pages, actions, ...';

function isUpEvent(e: KeyboardEvent) {
  return e.key === 'ArrowUp' || ((e.ctrlKey || e.metaKey) && e.key === 'k');
}

function isDownEvent(e: KeyboardEvent) {
  return e.key === 'ArrowDown' || ((e.ctrlKey || e.metaKey) && e.key === 'j');
}

function containsPowerSearchItem(nodes: NodeList) {
  return [...nodes].some(
    node =>
      node instanceof Element &&
      (node.classList.contains('power-search-item') || node.querySelector('.power-search-item') != null)
  );
}

type PowerSearchProps = PropsWithChildren<
  Omit<ModalProps, 'children' | 'close' | 'isOpen'> & {
    debounce?: number;
    onChange?(text: string): void;
    placeholder?: string;
  }
>;

export function PowerSearch({
  children,
  debounce = 500,
  onChange,
  placeholder = POWER_SEARCH_PLACEHOLDER,
  ...props
}: PowerSearchProps) {
  const { modal, setInputValue, inputValue, inputRef } = usePowerSearchContext()!;
  const [highlightedItemID, setHighlightedItemID] = useState('');
  const [activeGroupId, setActiveGroupId] = useState('');
  const [expandedGroupIds, setExpandedGroupIds] = useState<ReadonlySet<string>>(() => new Set());
  const resultsRef = useRef<HTMLDivElement>(null);
  const highlightedItemIndex = useRef(-1);
  const highlightedItemIDRef = useRef('');
  const toggledGroupIdRef = useRef('');
  const mixpanel = useMixpanel();

  const updateHighlightedID = useCallback((newID: string) => {
    setHighlightedItemID(newID);
    highlightedItemIDRef.current = newID;
    if (newID !== '') {
      const el = document.getElementById(newID);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const groupEl = el?.closest('[data-group-id]');
      const newGroupId = groupEl?.getAttribute('data-group-id') ?? '';
      setActiveGroupId(newGroupId);
    } else {
      setActiveGroupId('');
    }
  }, []);

  const activeGroupContextValue = useMemo(
    () => ({
      activeGroupId,
      expandedGroupIds,
      beforeGroupToggle: (groupId: string) => {
        toggledGroupIdRef.current = groupId;
      },
      toggleExpandedGroupId: (groupId: string) => {
        setExpandedGroupIds(prev => {
          const next = new Set(prev);
          if (next.has(groupId)) {
            next.delete(groupId);
          } else {
            next.add(groupId);
          }
          return next;
        });
      },
    }),
    [activeGroupId, expandedGroupIds]
  );

  const handlePressShortcut = useDynamicCallback(() => {
    if (modal.isOpen) {
      modal.close();
    } else {
      mixpanel.track(MixpanelEvent.OpenPowerSearch, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.KeyboardShortcut,
      });
      updateHighlightedID('');
      highlightedItemIndex.current = -1;
      modal.open();
    }
  });

  useOnKeyDown({ key: 'k', metaKey: true }, handlePressShortcut);
  useOnKeyDown({ key: 'k', ctrlKey: true }, handlePressShortcut);

  useDebounce(
    () => {
      onChange?.(inputValue);
    },
    debounce,
    [inputValue, onChange]
  );

  // Result groups render after the debounced search update, so observe their DOM changes and select the first available item.
  useEffect(() => {
    if (!modal.isOpen) {
      return;
    }
    const container = resultsRef.current;
    if (container == null) {
      return;
    }

    const highlightFirst = () => {
      const items = container.querySelectorAll<HTMLElement>('.power-search-item');
      if (items.length === 0) {
        updateHighlightedID('');
        highlightedItemIndex.current = -1;
      } else {
        updateHighlightedID(items[0].id);
        highlightedItemIndex.current = 0;
      }
    };

    highlightFirst();

    const observer = new MutationObserver(mutations => {
      const resultItemsChanged = mutations.some(
        mutation => containsPowerSearchItem(mutation.addedNodes) || containsPowerSearchItem(mutation.removedNodes)
      );
      if (!resultItemsChanged) {
        return;
      }

      const toggledGroupId = toggledGroupIdRef.current;
      if (toggledGroupId !== '') {
        toggledGroupIdRef.current = '';
        const items = container.querySelectorAll<HTMLElement>('.power-search-item');
        const currentID = highlightedItemIDRef.current;

        if (currentID !== '') {
          const currentIdx = [...items].findIndex(item => item.id === currentID);
          if (currentIdx !== -1) {
            highlightedItemIndex.current = currentIdx;
            return;
          }

          // Collapsing can remove the highlighted extra row, so retain focus in the same group.
          const groupEl = container.querySelector(`[data-group-id="${toggledGroupId}"]`);
          const groupItems = groupEl ? [...groupEl.querySelectorAll<HTMLElement>('.power-search-item')] : [];
          if (groupItems.length > 0) {
            const lastGroupItem = groupItems[groupItems.length - 1];
            highlightedItemIndex.current = [...items].indexOf(lastGroupItem);
            updateHighlightedID(lastGroupItem.id);
            return;
          }
        }
      }

      highlightFirst();
    });
    observer.observe(container, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [modal.isOpen, updateHighlightedID]);

  const handleKeyDown = useDynamicCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (resultsRef.current == null) {
      return;
    }
    const items = resultsRef.current.querySelectorAll('.power-search-item');
    const ids = [...items].map(item => item.id);

    if (isDownEvent(e) || isUpEvent(e)) {
      e.preventDefault();
      e.stopPropagation();
      if (items.length === 0) {
        return;
      }
      if (isDownEvent(e)) {
        highlightedItemIndex.current = (highlightedItemIndex.current + 1) % items.length;
      } else {
        highlightedItemIndex.current =
          highlightedItemIndex.current === 0 ? items.length - 1 : highlightedItemIndex.current - 1;
      }
      const id = ids[highlightedItemIndex.current];
      updateHighlightedID(id);
    } else if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else if (e.key === 'Enter') {
      if (e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        const id = ids[highlightedItemIndex.current];
        if (id != null) {
          const highlightedEl = document.getElementById(id);
          const groupEl = highlightedEl?.closest('[data-testid="power-search-group"]');
          const showMoreBtn = groupEl?.querySelector<HTMLButtonElement>(
            '[data-testid="power-search-show-more-button"]'
          );
          showMoreBtn?.click();
        }
      } else {
        const id = ids[highlightedItemIndex.current];
        if (id != null) {
          document.getElementById(id)?.click();
        }
      }
    }
  });

  const handleOnChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      setInputValue(e.target.value);
    },
    [setInputValue]
  );

  return (
    <Modal {...modal} width={800} maxWidth="90vw" closeOnEscape={inputValue === ''} mt="10vh" {...props}>
      <Stack maxHeight="80vh" overflow="hidden" onKeyDown={handleKeyDown}>
        <Input
          size={FormControlSizes.Large}
          value={inputValue}
          onChange={handleOnChange}
          placeholder={placeholder}
          clearable={true}
          autoFocus={true}
          style={INPUT_STYLES}
          ref={inputRef}
        />
        <Stack ref={resultsRef} overflow="auto" position="relative">
          <PowerSearchActiveGroupContext.Provider value={activeGroupContextValue}>
            <PowerSearchHighlightContext.Provider value={highlightedItemID}>
              {inputValue !== '' && children}
            </PowerSearchHighlightContext.Provider>
          </PowerSearchActiveGroupContext.Provider>
        </Stack>
        <Divider />
        <HStack justifyContent="space-between" p="spacing8">
          <Text>
            Use <PowerSearchShortcut /> to open power search
          </Text>
          <Text>
            <HStack gap="spacing4">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd>
              to navigate
            </HStack>
          </Text>
          <Text>
            <Kbd>⏎</Kbd> to select
          </Text>
        </HStack>
      </Stack>
    </Modal>
  );
}
